import { FC, useState, useMemo } from 'react';
import { Grid, Box, Typography, Chip, Button } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ArrowBack, Add } from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  useComplianceManagementState,
  useComplianceManagementActions,
} from '../../contexts/complianceManagement.context';
import ComplianceManagementDetails from './complianceManagementDetails';
import { useTranslation } from 'src/hooks/useTranslationHook';

enum EComplianceManagementView {
  GRID = 'grid',
  DETAILS = 'details',
}

interface ComplianceManagementGridProps {
  isLoading?: boolean;
}

const ComplianceManagementGridView: FC<ComplianceManagementGridProps> = ({
  isLoading: propIsLoading,
}) => {
  const [viewType, setViewType] = useState<EComplianceManagementView>(
    EComplianceManagementView.GRID
  );
  const {
    complianceList,
    isLoading,
    totalCount,
    paginationParams,
    selectedCompliance,
  } = useComplianceManagementState();
  const {
    updatePaginationParams,
    setSelectedComplianceId,
    setSelectedCompliance,
  } = useComplianceManagementActions();
  const { translateText } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'OVERDUE':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'WAIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRiskLevelColor = (riskLevel?: string) => {
    switch (riskLevel) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: translateText('settings.complianceManagement.columns.id'),
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">{params.row.id}</Typography>
          </Box>
        ),
      },
      {
        field: 'code',
        headerName: translateText('settings.complianceManagement.columns.code'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">
              {params.row.code ||
                translateText('settings.complianceManagement.values.nA')}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'title',
        headerName: translateText(
          'settings.complianceManagement.columns.title'
        ),
        width: 250,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight="medium">
              {params.row.title}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: translateText(
          'settings.complianceManagement.columns.status'
        ),
        width: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={params.row.status}
              size="small"
              color={getStatusColor(params.row.status) as any}
            />
          </Box>
        ),
      },
      {
        field: 'riskLevel',
        headerName: translateText(
          'settings.complianceManagement.columns.riskLevel'
        ),
        width: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.riskLevel ? (
              <Chip
                label={params.row.riskLevel}
                size="small"
                color={getRiskLevelColor(params.row.riskLevel) as any}
              />
            ) : (
              <Typography variant="body2">
                {translateText('settings.complianceManagement.values.nA')}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'priority',
        headerName: translateText(
          'settings.complianceManagement.columns.priority'
        ),
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.priority ? (
              <Chip
                label={params.row.priority}
                size="small"
                color={getPriorityColor(params.row.priority) as any}
              />
            ) : (
              <Typography variant="body2">
                {translateText('settings.complianceManagement.values.nA')}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'authority',
        headerName: translateText(
          'settings.complianceManagement.columns.authority'
        ),
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">
              {params.row.authority ||
                translateText('settings.complianceManagement.values.nA')}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'relatedEntityName',
        headerName: translateText(
          'settings.complianceManagement.columns.relatedEntity'
        ),
        width: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">
              {params.row.relatedEntityName ||
                translateText('settings.complianceManagement.values.nA')}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'primaryOwnerName',
        headerName: translateText(
          'settings.complianceManagement.columns.owner'
        ),
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">
              {params.row.primaryOwnerName ||
                translateText('settings.complianceManagement.values.nA')}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'dueDate',
        headerName: translateText(
          'settings.complianceManagement.columns.dueDate'
        ),
        width: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2">
              {params.row.dueDate
                ? new Date(params.row.dueDate).toLocaleDateString()
                : translateText('settings.complianceManagement.values.nA')}
            </Typography>
            {params.row.isOverdue && (
              <Chip
                label={translateText(
                  'settings.complianceManagement.columns.overdue'
                )}
                size="small"
                color="error"
                sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        ),
      },
      {
        field: 'daysToDue',
        headerName: translateText(
          'settings.complianceManagement.columns.daysToDue'
        ),
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.daysToDue !== null &&
              params.row.daysToDue !== undefined ? (
              <Typography
                variant="body2"
                color={params.row.daysToDue < 0 ? 'error' : 'textPrimary'}
              >
                {params.row.daysToDue}
              </Typography>
            ) : (
              <Typography variant="body2">
                {translateText('settings.complianceManagement.values.nA')}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'hasChecklist',
        headerName: translateText(
          'settings.complianceManagement.columns.checklist'
        ),
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={
                params.row.hasChecklist
                  ? translateText('settings.complianceManagement.values.yes')
                  : translateText('settings.complianceManagement.values.no')
              }
              size="small"
              variant="outlined"
              color={params.row.hasChecklist ? 'primary' : 'default'}
            />
          </Box>
        ),
      },
      {
        field: 'hasDocuments',
        headerName: translateText(
          'settings.complianceManagement.columns.documents'
        ),
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={
                params.row.hasDocuments
                  ? translateText('settings.complianceManagement.values.yes')
                  : translateText('settings.complianceManagement.values.no')
              }
              size="small"
              variant="outlined"
              color={params.row.hasDocuments ? 'primary' : 'default'}
            />
          </Box>
        ),
      },
    ],
    [translateText]
  );

  const isLoadingData = propIsLoading || isLoading;

  const handlePageChange = (page: number, pageSize: number) => {
    updatePaginationParams({ page, limit: pageSize });
  };

  const handleRowClick = (params: any) => {
    if (viewType === EComplianceManagementView.GRID) {
      const compliance = complianceList.find((c) => c.id === params.row.id);
      if (compliance) {
        setSelectedComplianceId(compliance.id);
        setViewType(EComplianceManagementView.DETAILS);
      }
    }
  };

  const handleBackToList = () => {
    setViewType(EComplianceManagementView.GRID);
    setSelectedComplianceId(null);
    setSelectedCompliance(null);
  };

  const handleAddCompliance = () => {
    // Handle add compliance logic
    console.log('Add compliance');
  };

  if (viewType === EComplianceManagementView.DETAILS) {
    return (
      <Grid
        container
        spacing={0}
        sx={{ height: '100%', overflow: 'hidden' }}
        data-testid="compliance-management-grid-details"
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 1 },
            gap: { xs: 1, sm: 0 },
            minHeight: { xs: 'auto', sm: '40px' },
          }}
          data-testid="compliance-management-grid-details-header"
        >
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToList}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: '36px' },
              }}
              data-testid="back-to-compliance-button"
            >
              {translateText('settings.complianceManagement.backToCompliance')}
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {selectedCompliance
                ? `${selectedCompliance.title} - ${translateText('settings.complianceManagement.details')}`
                : translateText('settings.complianceManagement.details')}
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: 'calc(100% - 48px)',
            overflow: 'hidden',
          }}
          data-testid="compliance-management-details-container"
        >
          <ComplianceManagementDetails onBack={handleBackToList} />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="compliance-management-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'stretch', sm: 'end' },
          mb: { xs: 1, sm: 2 },
        }}
        data-testid="compliance-management-grid-header"
      >
        {/* <Typography variant="h6">{translateText('settings.complianceManagement.title')}</Typography> */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddCompliance}
          sx={{
            minHeight: { xs: '44px', sm: '36px' },
            fontSize: { xs: '16px', sm: '14px' },
          }}
          data-testid="add-compliance-button"
        >
          {translateText('settings.complianceManagement.addCompliance')}
        </Button>
      </Grid>

      <Grid
        item
        xs={12}
        data-testid="compliance-management-grid-container"
        sx={{
          height: 'calc(100vh - 250px)',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          data-testid="compliance-management-grid-table"
          rows={complianceList}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={() => 'cursor-pointer'}
          loading={isLoadingData}
          serverSide={true}
          totalCount={totalCount}
          currentPage={paginationParams.page}
          currentPageSize={paginationParams.limit}
          onPageChange={handlePageChange}
          getRowId={(row) => row.id}
          hideColumnsOnMobile={['id', 'code', 'riskLevel', 'priority', 'authority', 'relatedEntityName', 'primaryOwnerName', 'hasChecklist', 'hasDocuments']}
        />
      </Grid>
    </Grid>
  );
};

export default ComplianceManagementGridView;
export { EComplianceManagementView };
